import React, { useContext, useState,useEffect } from "react";
import { useFormik } from "formik";
import { TaskCreateUpdateSchema} from "../../utils/validations";
import { InitiativeContext } from "../../contexts/InitiativeContextProvider";
import { createTask, updateTask,getCustomFields,getAllRisksWithFilters,getAllLessonsWithParams,getAllTaskComments } from "../../api/Api";
import { showToast } from "../../utils/toastUtils";
import CreateUpdateTaskModalUI from "./CreateUpdateTaskModalUI";
import { checkAdditionalCustomFields } from "./AddUpdateSubTask";


// list of Priorities
export const priorities = [
  { name: "High", value: "High" },
  { name: "Medium", value: "Medium" },
  { name: "Low", value: "Low" },
];

function AddUpdateTask({
  show,
  handleClose,
  projectId,
  workStreamId,
  is_new_task = true,
  task,
  loadAll
}) {
  const { data: workStreamData } = useContext(InitiativeContext);
  const [activeStep, setActiveStep] = React.useState(0); // Active step
  const [assignees, setAssignees] = useState([]); // Assignees
  const [workStreams, setWorkStreams] = useState([]); // WorkStreams
  const [freshTask, setFreshTask] = useState(null); // Fresh Task
  const [customFields, setCustomFields] = useState([]); // Custom Fields
  const [risks, setRisks] = useState([]);
  const [lessons,setLessons] = useState([]);
  const [comments,setComments]=useState([])

  const task_id = is_new_task?freshTask?.id:task?.id // to get the task id



  useEffect(() => {
    async function fetchCustomFields() {
      try {
        const response = await getCustomFields(projectId); // Fetch custom fields for the project
        setCustomFields(response?.data?.custom_fields || []);
        
      } catch (error) {
        console.error("Error fetching custom fields:", error);
        
      }
    }

    fetchCustomFields();
  }, [projectId]);

  const fetchRisks = async () => {
    if (task) {
      try {
        const response = await getAllRisksWithFilters(null, null, null, null, task?.id);
        if (response.status === 200) {
          setRisks(response.data);
        }
      } catch (error) {
        console.error("Error fetching risks:", error);
      }
    }
  };

   useEffect(() => {
    fetchRisks();
    }, [task]);

  const fetchLessons = async () => {
    if (task_id) {
      try {
        const response = await getAllLessonsWithParams( null, null, null, null, task_id);
        if (response.status === 200) {
          setLessons(response.data);
        }
      } catch (error) {
        console.error("Error fetching lessons:", error);
      }
    }
  };

   useEffect(() => {
    fetchLessons();
    }, [task]); 

  const fetchComments = async () => {
    if (task) {
      try {
        const response = await getAllTaskComments(task?.id);
        if (response.status === 200) {
          setComments(response.data);
        }
      } catch (error) {
        console.error("Error fetching lessons:", error);
      }
    }
  };

   useEffect(() => {
    fetchComments();
    }, [task]);   




  // Function to handle form submission
  const handleSubmit = async (values, closePop=false) => {
    formik.setSubmitting(true);
    
    // Check for empty description (strip HTML tags)
    const descriptionText = values.description ? values.description.replace(/<[^>]*>/g, '').trim() : '';
    const isDescriptionEmpty = !descriptionText;
    
    // Validate required fields
    const hasErrors = formik.errors.title || formik.errors.due_date;
    const isPriorityMissing = !values.priorities;
    const isDescriptionMissing = isDescriptionEmpty;

    if (hasErrors || isPriorityMissing || isDescriptionMissing) {
      showToast("Please fill all the required fields", "error");
      formik.setSubmitting(false);
      return;
    }

   
    // Construct the base task object
    const baseTask = {
      title: values.title,
      priority: values.priorities?.name || null,
      assignee_ids: assignees.map((assignee) => assignee.id),
      description: values.description,
      due_date: values.due_date,
      workStream_ids: workStreamId? [workStreamId]: workStreams.map((workStream) => workStream.id),
      custom_fields: customFields.map((field) => ({
        id: field.id,
        name: field.name,
        value: values[field.name],
      })),
    };

  
    try {
      let response;
      
      if (!task_id) {
        // Creating a new task
        const newTaskPayload = { ...baseTask, project_id: projectId };
        response = await createTask(newTaskPayload);
  
        if (response.status === 201) {
          showToast("Goal created successfully", "success");
          setFreshTask(response.data);
        }
      } else {
        // Updating an existing task
        const updateTaskPayload = { ...baseTask };
        response = await updateTask(task_id, updateTaskPayload);
  
        if (response.status === 200) {
          showToast("Goal updated successfully", "success");
          is_new_task ? setFreshTask(response.data) : handleClose();
        }
      }
  
      // Refresh the task list
      loadAll();
  
      if (closePop) {
        handleClose();
      }
    } catch (error) {
      console.error("Error submitting task:", error);
      showToast("Failed to create/update goal", "error");
    } finally {
      formik.setSubmitting(false);
    }
  };


  const formik = useFormik({
    initialValues: {
      title: is_new_task ? "" : task?.title,
      priorities:
        !is_new_task && task.priority ? { name: task.priority } : null,
      description: is_new_task ? "" : task.description || "", // Bind description
      due_date: is_new_task ? null : task.due_date || null, // Bind due_date
      ...customFields.reduce((acc, field) => {
        acc[field.name] = is_new_task ? "" :task?.custom_fields?.find((cf) => cf.name === field.name)?.value;
        console.log(acc)
        return acc;
      }, {}),
    },
    validationSchema:TaskCreateUpdateSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validate: (values) => {
      const errors = {};
      
      // Custom validation for description
      const descriptionText = values.description ? values.description.replace(/<[^>]*>/g, '').trim() : '';
      if (!descriptionText) {
        errors.description = "Description is required";
      }
      
      return errors;
    },
  });

  checkAdditionalCustomFields(customFields, task);
  
  return (
    <CreateUpdateTaskModalUI
      show={show}
      handleClose={handleClose}
      formik={formik}
      is_new_task={is_new_task}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      priorities={priorities}
      workStreamData={workStreamData}
      assignees={assignees}
      setAssignees={setAssignees}
      workStreams={workStreams}
      workStreamId={workStreamId} 
      setWorkStreams={setWorkStreams}
      handleSubmit={handleSubmit}
      task={task}
      task_id={task_id}
      customFields={is_new_task ? customFields : task?.custom_fields}
      risks={risks}
      fetchRisks={fetchRisks}
      lessons={lessons}
      fetchLessons={fetchLessons}
      comments={comments}
      fetchComments={fetchComments}
    />
  );
}

export default AddUpdateTask;
